import React, { useEffect, useState } from 'react';
import { Flag, Loader2, X, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { reviewsAPI } from '../services/api';

const FALLBACK = [
  { key: 'inapproprie', label: 'Contenu inapproprié' },
  { key: 'hors_sujet', label: 'Hors sujet' },
  { key: 'faux_avis', label: 'Faux avis' },
  { key: 'autre', label: 'Autre' },
];

const ReviewReportButton = ({ review, onReported }) => {
  const [open, setOpen] = useState(false);
  const [reasons, setReasons] = useState(FALLBACK);
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(review.reported);

  useEffect(() => { if (open) reviewsAPI.reportReasons().then(setReasons).catch(() => {}); }, [open]);

  if (done) {
    return <span className="inline-flex items-center gap-1 text-xs text-gray-400" data-testid={`review-reported-${review.id}`}><CheckCircle size={12} /> Signalé</span>;
  }

  const submit = async () => {
    setError('');
    if (!reason) { setError('Choisissez un motif'); return; }
    setBusy(true);
    try {
      await reviewsAPI.report(review.id, reason, comment);
      setDone(true);
      setOpen(false);
      onReported && onReported();
    } catch (e) {
      if (e.response?.status === 409) { setDone(true); setOpen(false); return; }
      setError(e.response?.data?.detail || 'Signalement impossible pour le moment');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={`review-report-${review.id}`} className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-red-600 transition-colors">
        <Flag size={12} /> Signaler
      </button>
      {open && (
        <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="review-report-modal">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Flag size={16} className="text-red-500" /> Signaler cet avis</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700" data-testid="review-report-close"><X size={20} /></button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-gray-600">Pourquoi souhaitez-vous signaler l'avis de <strong>{review.user_name}</strong> ? Notre équipe vérifiera avant toute action.</p>
              <div className="space-y-1.5">
                {reasons.map(r => (
                  <label key={r.key} className={`flex items-center gap-2 border rounded-md px-3 py-2 text-sm cursor-pointer transition-colors ${reason === r.key ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-orange-300'}`}>
                    <input type="radio" name={`report-reason-${review.id}`} value={r.key} checked={reason === r.key} onChange={() => setReason(r.key)} data-testid={`review-report-reason-${r.key}`} className="text-orange-500" />
                    {r.label}
                  </label>
                ))}
              </div>
              <textarea data-testid="review-report-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={2} maxLength={500} placeholder="Précisions (facultatif)" className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              {error && <p className="text-sm text-red-600" data-testid="review-report-error">{error}</p>}
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-2 rounded-b-xl">
              <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Annuler</Button>
              <Button size="sm" onClick={submit} disabled={busy} data-testid="review-report-submit" className="bg-red-600 hover:bg-red-700">
                {busy && <Loader2 size={14} className="mr-2 animate-spin" />} Envoyer le signalement
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReviewReportButton;
